import React, { Component } from 'react';
import dayjs from '../../dayjs/dayjs_timezone.js';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import ErrorTable from './reservationPage/ErrorTable';
import Timer from '../components/Timer';
import { parseErrors } from './reservationPage/parser';
import { formatDate } from '../utilities/timeUtility';
import Translation from '../utilities/localizationUtility';
import { demoData } from '../demoData/reservation.js';
import { WidgetSetting } from '../types';
import Spinner from '../components/Spinner';

export default class ReservationPage extends Component {
  state = {
    isShowError: false,
    isAnyErrorItem: false,
    formError: '',
    items: [],
    expiresAt: '',
    leftSeconds: 0,
    isLoading: false
  };

  onExpired = this.onExpired.bind(this);

  UNSAFE_componentWillMount() {
    if (window.xprops) {
      window.xprops.onProps(this.onXChildData.bind(this));
    }
  }

  componentDidUpdate() {
    const { onTrigger, setting } = this.props,
          { expiresAt, items, leftSeconds } = this.state;
    if (!+setting.when_expired && items.length && expiresAt && leftSeconds === 0) {
      onTrigger('removeItems', items.map(x => x.external_id));
    }
  }

  onNewItems({ items, checkout = false, expiresAt = '', leftSeconds = 0 }) {
    const isAnyErrorItem = items.some(item => item.status !== 'ok'),
          { onTrigger, setting: { position } } = this.props,
          hideWidget = !isAnyErrorItem && (position === 'None');

    if (checkout && !isAnyErrorItem) {
      onTrigger('checkout');
    } else {
      onTrigger('enableCheckout', !isAnyErrorItem);
      const trigger = items.length && !hideWidget ? 'show' : 'hide';
      this.setState({ items, isAnyErrorItem, expiresAt, leftSeconds, isLoading: false });
      onTrigger(trigger);
    }
  }

  onXChildData(input) {
    let data;
    try {
      if (input) {
        data = input.data;
      }
      if (!data) return;

      if (this.props.isDemo) {
        this.onNewItems(demoData());
        return;
      }

      if (!data.items) return;

      const { checkout, items, checkExpires } = data,
            server_error_statuses = new Array(527 - 500).fill(undefined).map((_, i) => i + 500),
            { reservationActions: { submitReservation }, onTrigger } = this.props;

      data.checkExpires = checkExpires ? this.state.expiresAt : null

      submitReservation(data)
        .then(({ data: { reservation: { cart_token: cartToken, expires_at: expiresAt, items: newItems, left_seconds: leftSeconds } } }) => {
          const newCartToken = newItems.length ? cartToken : null;
          onTrigger('updateCartToken', newCartToken);
          this.onNewItems({
            items: newItems,
            checkout,
            expiresAt,
            leftSeconds,
          });
        })
        .catch((response) => {
          const error = response.response;
          // show message if js function broken
          this.setState({formError: ''})
          if (!error) {
            // here is a trick to make promise error to trigger window.onerror
            setTimeout(() => {
              throw response;
            });
          } else if (error.status === 401) {
            this.onNewItems({ items: [], checkout });
          } else if (error.status === 410) {
            onTrigger('checkout');
          } else if (server_error_statuses.includes(error.status)) { // Codes 5xx
            console.log(`%c[BTA: reservation widget] Failed to save reservation:
                          ${error.status} (${error.statusText})
                          Please contact support@zetya.com for further assistance.`, "color: red")
          } else if (error.data) {
            if (error.data.form) {
              this.setState({formError: error.data.form})
            } else {
              this.onNewItems({ items: parseErrors(items, error) });
            }
          }
        });
    } catch (e) {
      // here is a trick to make promise error to trigger window.onerror
      setTimeout(() => {
        throw e;
      });
    }
  }

  onExpired() {
    const { onTrigger, setting } = this.props;

    if (!+setting.when_expired) {
      const { items } = this.state;
      onTrigger('removeItems', items.map(x => x.external_id));
    } else {
      // trigger re-render
      onTrigger('enableCheckout', false);
      this.setState({leftSeconds: 0});
    }
  }

  onShowError() {
    const { isShowError } = this.state;
    this.setState({ isShowError: !isShowError });
  }

  renderErrorItem(items) {
    const { isShowError } = this.state;
    const t = new Translation(this.props);
    const { style } = this.props;
    return (
      <div style={window.screen.width > 576 ? { padding: '0 15px', flexGrow: 1 } : { flexGrow: 1 }}>
        <ErrorTable style={style} show={isShowError} items={items} translation={t} />

        <div className="flexbox vertical-center space-between">
          <p style={this.textStyle({})}>{t.key('bookingNotReserved')}</p>
          <input
            type="button"
            style={this.buttonStyle({ margin: '0 1em' })}
            onClick={this.onShowError.bind(this)}
            className="pure-button fz14"
            value={t.key('updateReservation')}
          />
        </div>
      </div>
    );
  }

  buttonStyle(styles) {
    const { style, defaultSetting } = this.props;
    const theme = defaultSetting.theme || {};
    return Object.assign({}, styles, style.body, { color: theme.buttonTextColor, background: theme.buttonColor });
  }

  textStyle(styles) {
    const { style, defaultSetting } = this.props;
    const theme = defaultSetting.theme || {};
    return Object.assign({}, styles, style.body, { color: theme.textColor });
  }

  layoutStyle(styles) {
    const { style, defaultSetting } = this.props;
    const theme = defaultSetting.theme || {};
    return Object.assign({}, styles, style.body, { color: theme.textColor, background: theme.backgroundColor });
  }

  onRetry() {
    const spinner = this.spinner;
    this.setState({isLoading: true});
    if (spinner) spinner.show();
    this.props.onTrigger('refresh');
  }

  renderRetryButton(value) {
    return (
      <React.Fragment>
        <Spinner ref={ref => (this.spinner = ref)}/>
        <input
          type="button"
          style={{ margin: '0 1em' }}
          onClick={() => this.onRetry()}
          className="expired pure-button fz14"
          value={value ? value : "Retry"}
          disabled={this.state.isLoading}
        />
      </React.Fragment>
    )
  }

  render() {
    const {
      style, setting: {
        position, mode, when_expired, reservation_expiry_minutes,
      }, onTrigger, timeZone, defaultSetting
    } = this.props;
    const {
      isShowError, formError, isAnyErrorItem, items, expiresAt, leftSeconds
    } = this.state;
    const textStyle = this.textStyle({ margin: '1em' });
    const layoutStyle = this.layoutStyle({ padding: '5px 15px', opacity: 0.98 });
    if (['Bottom', 'None'].includes(position) || !position) layoutStyle.transform = 'rotate(180deg)';
    const t = new Translation(this.props);

    if (items.length > 0) {
      return (
        <div className="flexbox space-between vertical-center" style={layoutStyle}>
          {
            isAnyErrorItem
              ? this.renderErrorItem(items)
              : (
                <div style={{ flexGrow: 1 }}>
                  {expiresAt && leftSeconds == 0 ? onTrigger('enableCheckout', false) && (
                    <div className="flexbox space-between vertical-center">
                      <p style={textStyle}>{t.key('reservationExpired')}</p>
                      {!isEmpty(this.props.setting) && !!+when_expired &&
                        this.renderRetryButton(t.key('retryButtonLabel'))
                      }
                    </div>
                  ) : (
                    <div className="flexbox space-between vertical-center">
                      {formError ? (
                          <p style={textStyle}>
                            {t.key(formError)}
                          </p>
                        )
                        : (
                        <>
                          <p style={textStyle}>
                            {t.key('success')}
                            {' '}
                            {formatDate(expiresAt, timeZone)}
                          </p>
                          <div style={Object.assign({}, textStyle, { flexBasis: '80px' })}>
                            <Timer seconds={leftSeconds} onExpired={this.onExpired} />
                          </div>
                        </>
                        )
                      }
                    </div>
                  )}
                </div>
              )
          }
          <input
            type="button"
            style={this.buttonStyle({ fontSize: '24px', padding: '0 8px 2px' })}
            className="pure-button fz14"
            onClick={() => onTrigger('hide')}
            value="&times;"
          />
        </div>
      );
    }

    return null;
  }
}

ReservationPage.propTypes = {
  timeZone: PropTypes.string.isRequired,
  translation: PropTypes.object.isRequired,
  defaultTranslation: PropTypes.object.isRequired,
  setting: WidgetSetting,
  onTrigger: PropTypes.func,
  reservationActions: PropTypes.shape({ submitReservation: PropTypes.func }).isRequired,
  style: PropTypes.object.isRequired,
};
